import React from "react";

/**
 * SpendNova brand logo.
 * - `horizontal` / `stacked` variants render the official Composite Logo asset
 *   (mark + wordmark) inside a rounded navy chip.
 * - `mark` variant renders the SVG standalone "S" (with green growth swoosh +
 *   ascending bar chart). Use `markVariant="light"` on dark backgrounds.
 */

const LOGO_SRC = "/spendnova-logo-2400.webp";

const SMark = ({ size = 48, variant = "dark", strokeWidth }) => {
  const uid = React.useId();
  const gGreen = `snGreen-${uid}`;
  const sColor = variant === "light" ? "#FFFFFF" : "#0B1D3A";
  const sw = strokeWidth || (variant === "light" ? 7 : 6);

  return (
    <svg
      viewBox="0 0 72 72"
      width={size}
      height={size}
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient id={gGreen} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#22C585" />
          <stop offset="100%" stopColor="#34E39A" />
        </linearGradient>
      </defs>

      {/* Ascending bar chart (behind the S) */}
      <g fill={`url(#${gGreen})`}>
        <rect x="27" y="46" width="3.5" height="7" rx="1.2" />
        <rect x="33" y="42" width="3.5" height="11" rx="1.2" />
        <rect x="39" y="38" width="3.5" height="15" rx="1.2" />
      </g>

      {/* Green growth swoosh */}
      <path
        d="M9 52c6 8 16 12 27 12 12 0 23-5 28-14"
        fill="none"
        stroke={`url(#${gGreen})`}
        strokeWidth="5.5"
        strokeLinecap="round"
      />
      {/* Arrow tip */}
      <path
        d="M58 38l7 12-13 2"
        fill="none"
        stroke={`url(#${gGreen})`}
        strokeWidth="5.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* S */}
      <path
        d="M51 22c-3.7-4-9-6-14.7-6-8.6 0-14.6 4.6-14.6 11.2 0 5.8 4.2 8.7 13.2 10.7 6.6 1.5 9 2.6 9 5.4 0 2.9-3.4 4.9-8.6 4.9-5.1 0-9.4-1.9-12.3-5.4"
        fill="none"
        stroke={sColor}
        strokeWidth={sw}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const LogoBadge = ({ height = 40, padding = 10, radius = 12, shadow = true }) => (
  <div
    className="inline-flex items-center justify-center"
    style={{
      background: "#0B1D3A",
      padding: `${padding}px ${padding * 1.3}px`,
      borderRadius: radius,
      boxShadow: shadow
        ? "0 6px 18px -8px rgba(11,29,58,0.35), inset 0 1px 0 rgba(255,255,255,0.06)"
        : "none",
    }}
  >
    <img
      src={LOGO_SRC}
      alt="SpendNova™"
      style={{
        height,
        width: "auto",
        display: "block",
        objectFit: "contain",
      }}
    />
  </div>
);

const Logo = ({ variant = "horizontal", size = 40, markVariant = "dark" }) => {
  if (variant === "mark") {
    return <SMark size={size} variant={markVariant} />;
  }

  if (variant === "stacked") {
    return (
      <div className="inline-block" data-testid="sn-logo-stacked">
        <LogoBadge height={Math.max(48, size)} padding={14} radius={16} />
      </div>
    );
  }

  return (
    <div className="inline-flex items-center" data-testid="sn-logo">
      <LogoBadge height={size} padding={size * 0.28} radius={12} />
    </div>
  );
};

export default Logo;
export { SMark };
